import { ReactNode } from 'react'
import styles from './AiAssistant.module.scss'

/**
 * Renders the small markdown subset the model answers in — paragraphs, **bold**,
 * `code`, headings, bullet/numbered lists, and pipe tables — as React elements.
 * No HTML is ever injected, so text from the model (or from the data it read)
 * cannot inject markup.
 */
export const AnswerText = ({ text }: { text: string }) => {
  return <div className={styles.answerText}>{toBlocks(text)}</div>
}

const inline = (text: string): ReactNode[] =>
  text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (part.length > 4 && part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.length > 2 && part.startsWith('`') && part.endsWith('`')) return <code key={i}>{part.slice(1, -1)}</code>
    return part
  })

const cells = (line: string) =>
  line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim())

const isSeparatorRow = (line: string) => cells(line).every((cell) => /^:?-{2,}:?$/.test(cell))

const BULLET = /^\s*[-*•]\s+/
const NUMBERED = /^\s*\d+[.)]\s+/

function toBlocks(text: string): ReactNode[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    if (!line.trim()) {
      i++
      continue
    }

    if (line.trim().startsWith('|')) {
      const rows: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('|')) rows.push(lines[i++])
      const [head, ...body] = rows.filter((row) => !isSeparatorRow(row))
      if (!head) continue
      blocks.push(
        <table key={blocks.length} className={styles.markdownTable}>
          <thead>
            <tr>{cells(head).map((cell, c) => <th key={c}>{inline(cell)}</th>)}</tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>{cells(row).map((cell, c) => <td key={c}>{inline(cell)}</td>)}</tr>
            ))}
          </tbody>
        </table>,
      )
      continue
    }

    const heading = /^\s*#{1,6}\s+(.*)$/.exec(line)
    if (heading) {
      blocks.push(<p key={blocks.length} className={styles.answerHeading}>{inline(heading[1])}</p>)
      i++
      continue
    }

    if (BULLET.test(line) || NUMBERED.test(line)) {
      const numbered = NUMBERED.test(line)
      const marker = numbered ? NUMBERED : BULLET
      const items: string[] = []
      while (i < lines.length && marker.test(lines[i])) items.push(lines[i++].replace(marker, ''))
      const List = numbered ? 'ol' : 'ul'
      blocks.push(
        <List key={blocks.length}>
          {items.map((item, n) => <li key={n}>{inline(item)}</li>)}
        </List>,
      )
      continue
    }

    const paragraph: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trim().startsWith('|') &&
      !BULLET.test(lines[i]) &&
      !NUMBERED.test(lines[i]) &&
      !/^\s*#{1,6}\s+/.test(lines[i])
    ) {
      paragraph.push(lines[i++])
    }
    blocks.push(
      <p key={blocks.length}>
        {paragraph.map((part, n) => (
          <span key={n}>
            {n > 0 && <br />}
            {inline(part)}
          </span>
        ))}
      </p>,
    )
  }

  return blocks
}
